/**
 * Token Retry Utility
 *
 * Handles exponential backoff retry logic for JWT token availability.
 * Addresses race conditions where GraphQL requests execute before JWT tokens
 * are fully written to browser storage.
 *
 * @see https://github.com/PilotCityInc/pcv4maxpro/pull/3551
 * @see https://pilotcity.sentry.io/issues/6993845687
 */

import { getAccessToken } from '@/services/secureStorage';
import * as Sentry from '@sentry/vue';
import pRetry from 'p-retry';

export interface TokenRetryOptions {
  /** Context identifier for logging and tracking */
  context: string;
  /** Maximum number of retry attempts (default: 20) */
  maxAttempts?: number;
  /** Additional data to include in Sentry breadcrumbs/events */
  metadata?: Record<string, unknown>;
  /** Optional logger for console output */
  logger?: {
    log: (message: string) => void;
    warn: (message: string) => void;
    error: (message: string) => void;
  };
}

export interface TokenRetryResult {
  /** The retrieved token, or null if unavailable after all retries */
  token: string | null;
  /** Number of attempts made */
  attempts: number;
  /** Total time waited in milliseconds */
  totalWaitMs: number;
  /** Whether retry attempts were exhausted without finding token */
  exhausted: boolean;
}

/**
 * Wait for authentication token with exponential backoff retry logic.
 *
 * Uses p-retry library for robust exponential backoff implementation.
 * - Progression: 100ms → 150ms → 225ms → 337ms → 500ms (capped)
 * - Max attempts: 20 (default)
 * - Max wait time: ~8.8 seconds
 *
 * @param options - Configuration for retry behavior and logging
 * @returns Result object containing token and retry metrics
 *
 * @example
 * ```typescript
 * // Apollo auth link usage
 * const result = await waitForToken({
 *   context: 'apollo_auth_link',
 *   logger: console
 * });
 * if (!result.token) {
 *   // Handle token unavailable after all retries
 * }
 * ```
 *
 * @example
 * ```typescript
 * // Profile submission usage
 * const result = await waitForToken({
 *   context: 'profile_submission',
 *   metadata: { phase: 'general_profile' }
 * });
 * ```
 */
export async function waitForToken(options: TokenRetryOptions): Promise<TokenRetryResult> {
  const { context, maxAttempts = 20, metadata = {}, logger } = options;

  // Check if token is immediately available
  const initialToken = getAccessToken();
  if (initialToken) {
    return {
      token: initialToken,
      attempts: 0,
      totalWaitMs: 0,
      exhausted: false
    };
  }

  // Token not immediately available - begin exponential backoff
  if (logger?.warn) {
    logger.warn(
      `[${context}] Token not immediately available, waiting with exponential backoff...`
    );
  }

  Sentry.addBreadcrumb({
    category: context.includes('apollo') ? 'apollo' : 'auth',
    message: `Token not immediately available for ${context}`,
    level: 'warning',
    data: metadata
  });

  // Track retry metrics
  let attempts = 0;
  const startTime = performance.now();

  try {
    const token = await pRetry(
      async () => {
        attempts++;
        const token = getAccessToken();

        if (!token) {
          // Token not available yet - throw to trigger retry
          throw new Error('Token not yet available');
        }

        // Success - token retrieved
        return token;
      },
      {
        retries: maxAttempts,
        factor: 1.5,
        minTimeout: 100,
        maxTimeout: 500,
        randomize: false,
        onFailedAttempt: error => {
          if (logger?.warn) {
            logger.warn(
              `[${context}] Attempt ${error.attemptNumber} failed. ${error.retriesLeft} retries left.`
            );
          }
        }
      }
    );

    const totalWaitMs = performance.now() - startTime;

    // Success - token retrieved after retry
    if (logger?.log) {
      logger.log(
        `[${context}] Token became available after ${totalWaitMs.toFixed(
          0
        )}ms (${attempts} attempts)`
      );
    }

    Sentry.captureMessage(`Token retrieved after retry: ${context}`, {
      level: 'info',
      tags: {
        auth_context: context,
        retry_success: 'true'
      },
      extra: {
        attempts,
        waitTimeMs: totalWaitMs,
        ...metadata
      }
    });

    return {
      token,
      attempts,
      totalWaitMs,
      exhausted: false
    };
  } catch (error) {
    // Token still unavailable after all retries
    const totalWaitMs = performance.now() - startTime;

    if (logger?.error) {
      logger.error(`[${context}] No token available after waiting ~${totalWaitMs.toFixed(0)}ms`);
    }

    Sentry.captureMessage(`Token unavailable after exponential backoff: ${context}`, {
      level: 'error',
      tags: {
        auth_context: context,
        retry_exhausted: 'true'
      },
      extra: {
        totalWaitTimeMs: totalWaitMs,
        attempts,
        maxAttempts,
        ...metadata
      }
    });

    return {
      token: null,
      attempts,
      totalWaitMs,
      exhausted: true
    };
  }
}
